/** Column visibility + reorder for configurable data tables (web + extension). */

import {
  filterPickerColumns,
  finalizePinnedColumnOrder,
  isDefaultPinnedColumnKey,
  movePickerColumn,
  pickerColumnLabel,
  pinnedColumnKeys,
  rebuildPickerColumnOrder,
  toggleColumnPinned,
} from "./columnPicker";
import {
  isTableShadingEnabled,
  renderTableShadingPrefRow,
  setTableShadingEnabled,
} from "./tableShading";
import { effectiveWebTableId, isWebCompactMode } from "./webCompactMode";
import { getTableColumnDefaults, type TableColumnId } from "./tableColumnRegistry";

export interface ColumnDefinition {
  key: string;
  label: string;
  /** Short flat label for the column picker (defaults to `label`, minus stacked suffixes). */
  pickerLabel?: string;
  /** Always visible and pinned in order (e.g. ticker, expand). */
  locked?: boolean;
  headerClass?: string;
  /** Raw header cell HTML (label used when omitted). */
  headerHtml?: string;
  /** Optional colgroup class (Calls+ etc.). */
  colClass?: string;
  /** Optional fixed col width (e.g. `28px` for action dots). */
  colWidth?: string;
  /** Stacked under another column — hidden from column picker. */
  sublineOf?: string;
  /** Hidden from column picker (e.g. action columns). */
  pickerHidden?: boolean;
  /** Omitted from compact layout (data shown stacked elsewhere). Hidden from picker in compact mode. */
  compactHidden?: boolean;
}

export interface ColumnState extends ColumnDefinition {
  visible: boolean;
  /** Stick to the left while the table scrolls horizontally. */
  pinned?: boolean;
}

const MODAL_ID = "sfe-column-settings-modal";

let activeDraft: ColumnState[] = [];
let activeTableId = "";
let activeDefaults: ColumnDefinition[] = [];
let activeOnApply: ((columns: ColumnState[]) => void) | null = null;
let modalBound = false;
let tableShadingDraft = false;

function storageKey(tableId: string): string {
  return `sfe-table-columns-${tableId}-v1`;
}

function finalizeColumnOrder(_tableId: string, columns: ColumnState[]): ColumnState[] {
  return finalizePinnedColumnOrder(columns);
}

function withDefaultPinned(column: ColumnDefinition, visible: boolean, pinned?: boolean): ColumnState {
  return {
    ...column,
    visible,
    pinned: pinned ?? isDefaultPinnedColumnKey(column.key),
  };
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function cloneColumns(columns: ColumnState[]): ColumnState[] {
  return columns.map((column) => ({ ...column }));
}

export function loadTableColumns(tableId: string, defaults: ColumnDefinition[]): ColumnState[] {
  const base = defaults.map((column) => withDefaultPinned(column, true));
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (!raw) return finalizeColumnOrder(tableId, base);
    const savedRaw = JSON.parse(raw) as { key?: string; visible?: boolean; pinned?: boolean }[];
    if (!Array.isArray(savedRaw) || !savedRaw.length) return finalizeColumnOrder(tableId, base);

    // History(L): Broker was renamed to System — keep prior visibility/order.
    const hasSystemDefault = defaults.some((column) => column.key === "system");
    const hasBrokerDefault = defaults.some((column) => column.key === "broker");
    const saved = savedRaw.map((entry) =>
      entry.key === "broker" && hasSystemDefault && !hasBrokerDefault
        ? { ...entry, key: "system" }
        : entry,
    );

    const byKey = new Map(saved.filter((entry) => entry.key).map((entry) => [entry.key!, entry]));
    const ordered = saved
      .map((entry) => defaults.find((column) => column.key === entry.key))
      .filter((column): column is ColumnDefinition => Boolean(column))
      .map((column) => {
        const savedEntry = byKey.get(column.key);
        return withDefaultPinned(
          column,
          column.locked === true ||
            column.key === defaults.find((d) => d.locked)?.key ||
            savedEntry?.visible !== false,
          typeof savedEntry?.pinned === "boolean" ? savedEntry.pinned : undefined,
        );
      });

    // Columns added since the order was saved land beside their registry neighbour, so a
    // new column shows up where it was designed to sit instead of after the row menu.
    const seen = new Set(ordered.map((column) => column.key));
    for (const [index, column] of defaults.entries()) {
      if (seen.has(column.key)) continue;
      const prevKey = index > 0 ? defaults[index - 1].key : null;
      const prevAt = prevKey ? ordered.findIndex((entry) => entry.key === prevKey) : -1;
      const insertAt = prevKey === null ? 0 : prevAt >= 0 ? prevAt + 1 : ordered.length;
      ordered.splice(insertAt, 0, withDefaultPinned(column, true));
      seen.add(column.key);
    }
    return finalizeColumnOrder(tableId, ordered);
  } catch {
    return finalizeColumnOrder(tableId, base);
  }
}

export function saveTableColumns(tableId: string, columns: ColumnState[]): void {
  const ordered = finalizeColumnOrder(tableId, columns);
  localStorage.setItem(
    storageKey(tableId),
    JSON.stringify(
      ordered.map(({ key, visible, pinned }) => ({
        key,
        visible,
        pinned: pinned === true,
      })),
    ),
  );
}

export function toggleTableColumnPin(
  columns: ColumnState[],
  key: string,
  pinned: boolean,
): ColumnState[] {
  return toggleColumnPinned(columns, key, pinned);
}

export function tablePinnedKeys(columns: ColumnState[]): string[] {
  return pinnedColumnKeys(visibleTableColumns(columns));
}

export function visibleTableColumns(columns: ColumnState[]): ColumnState[] {
  return columns.filter((column) => column.visible || column.locked);
}

/** Table body/header columns — in compact mode omit subline companions (rendered stacked). */
export function displayTableColumns(columns: ColumnState[], compact: boolean): ColumnState[] {
  const visible = visibleTableColumns(columns);
  if (!compact) return visible;
  return visible.filter((column) => !column.sublineOf && !column.compactHidden);
}

/** Whether a cell value counts as empty for compact column hiding. */
export function isCompactTableValueEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "number") return !Number.isFinite(value);
  if (typeof value === "string") {
    const t = value.trim();
    return !t || t === "—" || t === "–" || t === "-" || /^n\/a$/i.test(t);
  }
  return false;
}

/** Compact layout: hide columns with no meaningful data in any visible row. */
export function displayTableColumnsForData<T>(
  columns: ColumnState[],
  compact: boolean,
  rows: T[],
  valueForColumn: (row: T, key: string) => unknown,
): ColumnState[] {
  const visible = displayTableColumns(columns, compact);
  if (!compact || !rows.length) return visible;

  return visible.filter((column) => {
    if (column.locked) return true;
    return rows.some((row) => !isCompactTableValueEmpty(valueForColumn(row, column.key)));
  });
}

export function pickerTableColumns(columns: ColumnState[]): ColumnState[] {
  return filterPickerColumns(columns, { compact: isWebCompactMode() });
}

function rebuildColumnOrder(columns: ColumnState[], pickerOrder: string[]): ColumnState[] {
  return rebuildPickerColumnOrder(columns, pickerOrder);
}

export function normalizeTableColumnOrder(columns: ColumnState[]): ColumnState[] {
  const pickerOrder = pickerTableColumns(columns).map((column) => column.key);
  return rebuildColumnOrder(columns, pickerOrder);
}

export function moveTableColumn(
  columns: ColumnState[],
  index: number,
  direction: "up" | "down",
): ColumnState[] {
  return finalizePinnedColumnOrder(
    movePickerColumn(columns, index, direction, { compact: isWebCompactMode() }),
  );
}

export function toggleTableColumn(
  columns: ColumnState[],
  key: string,
  visible: boolean,
): ColumnState[] {
  const compact = isWebCompactMode();
  return columns.map((column) => {
    if (column.locked) return column;
    if (column.key === key) return { ...column, visible };
    if (compact && column.sublineOf === key) return { ...column, visible };
    return column;
  });
}

export function renderTableHeadRow(columns: ColumnState[]): string {
  return visibleTableColumns(columns)
    .map((column) => {
      if (column.headerHtml) return wrapTableHeadCell(column.headerHtml);
      const cls = column.headerClass ? ` class="${column.headerClass}"` : "";
      const colAttr = column.key ? ` data-col="${esc(column.key)}"` : "";
      return `<th${cls}${colAttr}><span class="th-inner"><span class="th-label">${esc(column.label)}</span></span></th>`;
    })
    .join("");
}

function addTickerHeaderClass(html: string): string {
  if (!/data-col="(?:ticker|underlying_ticker)"/.test(html)) return html;
  return html.replace(/<th\b([^>]*)>/i, (_match, attrs: string) => {
    if (/class="([^"]*)"/.test(attrs)) {
      return `<th${attrs.replace(/class="([^"]*)"/, (_m, cls: string) => {
        return cls.includes("sfe-ticker-col-header")
          ? `class="${cls}"`
          : `class="${cls} sfe-ticker-col-header"`;
      })}>`;
    }
    return `<th class="sfe-ticker-col-header"${attrs}>`;
  });
}

function wrapTableHeadCell(html: string): string {
  let out = html;
  if (!out.includes("th-label")) {
    out = out.replace(
      /(<th\b[^>]*>)([\s\S]*?)(<\/th>)/i,
      (_match, open: string, inner: string, close: string) => {
        const trimmed = inner.trim();
        if (!trimmed || inner.includes("th-inner")) return open + inner + close;
        return `${open}<span class="th-inner"><span class="th-label">${inner}</span></span>${close}`;
      },
    );
  }
  return addTickerHeaderClass(out);
}

export function renderTableColgroup(
  columns: ColumnState[],
  opts?: { lockWidths?: boolean },
): string {
  const lock = opts?.lockWidths === true;
  const cols = visibleTableColumns(columns)
    .map((column) => {
      const cls = column.colClass ? ` class="${column.colClass}"` : "";
      const colAttr = column.key ? ` data-col="${esc(column.key)}"` : "";
      const pinnedAttr = column.pinned ? ` data-pinned="1"` : "";
      if (!column.colWidth) return `<col${cls}${colAttr}${pinnedAttr} />`;
      const width = lock
        ? String(column.colWidth).endsWith("%")
          ? ` style="width:${column.colWidth}"`
          : ` style="width:${column.colWidth};min-width:${column.colWidth};max-width:${column.colWidth}"`
        : ` style="width:${column.colWidth}"`;
      return `<col${cls}${colAttr}${pinnedAttr}${width} />`;
    })
    .join("");
  return cols ? `<colgroup>${cols}</colgroup>` : "";
}

/** `data-sfe-pinned` for tables that apply sticky stacking after render. */
export function renderTablePinnedAttr(columns: ColumnState[]): string {
  const keys = tablePinnedKeys(columns);
  return keys.length ? ` data-sfe-pinned="${keys.map(esc).join(",")}"` : ` data-sfe-pinned=""`;
}

function renderColumnSettingsBody(
  columns: ColumnState[],
  defaults: ColumnDefinition[],
  sectionId = "",
): string {
  const sectionAttr = sectionId ? ` data-section="${esc(sectionId)}"` : "";
  const tableId = sectionId || activeTableId || "";
  const orderedColumns = tableId ? finalizeColumnOrder(tableId, columns) : columns;
  const defaultByKey = new Map(defaults.map((column) => [column.key, column]));
  const pickerCols = pickerTableColumns(orderedColumns);
  return pickerCols
    .map((column, index) => {
      const locked = column.locked === true;
      const fallback = defaultByKey.get(column.key);
      const label = pickerColumnLabel({
        ...column,
        label: column.label || fallback?.label || column.key,
        pickerLabel: column.pickerLabel ?? fallback?.pickerLabel,
      });
      const pinned = column.pinned === true;
      const pinButton = `<button type="button" class="small ghost sfe-column-pin-btn${pinned ? " is-pinned" : ""}" data-action="column-pin"${sectionAttr} data-key="${esc(column.key)}" aria-pressed="${pinned ? "true" : "false"}" aria-label="${pinned ? "Unpin" : "Pin"} ${esc(label)}" title="${pinned ? "Unpin column" : "Pin column while scrolling"}">📌</button>`;
      const orderButtons = locked
        ? `<em class="sfe-column-settings-fixed">Fixed</em>`
        : `<button type="button" class="small ghost" data-action="column-up"${sectionAttr} data-index="${index}" ${index <= 0 || pickerCols[index - 1]?.locked ? "disabled" : ""} aria-label="Move ${esc(label)} up">↑</button>
           <button type="button" class="small ghost" data-action="column-down"${sectionAttr} data-index="${index}" ${index >= pickerCols.length - 1 || pickerCols[index + 1]?.locked ? "disabled" : ""} aria-label="Move ${esc(label)} down">↓</button>`;
      return `
        <div class="sfe-column-settings-row${locked ? " sfe-column-settings-row-locked" : ""}${pinned ? " sfe-column-settings-row-pinned" : ""}">
          <label class="sfe-column-settings-check">
            <input type="checkbox" data-column-toggle="${esc(column.key)}"${sectionAttr} ${column.visible ? "checked" : ""} ${locked ? "disabled" : ""} aria-label="${esc(label)}" />
          </label>
          <span class="sfe-column-settings-name">${esc(label)}</span>
          <span class="sfe-column-settings-order">${orderButtons}${pinButton}</span>
        </div>`;
    })
    .join("");
}

function paintColumnSettingsSection(
  host: HTMLElement,
  sectionId: string,
  columns: ColumnState[],
  defaults: ColumnDefinition[],
): void {
  const section = host.querySelector<HTMLElement>(`[data-column-section="${sectionId}"] .sfe-column-settings-list`);
  if (section) section.innerHTML = renderColumnSettingsBody(columns, defaults, sectionId);
}

export function openColumnSettingsModal(options: {
  tableId: string;
  title: string;
  defaults: ColumnDefinition[];
  onApply: (columns: ColumnState[]) => void;
}): void {
  activeTableId = options.tableId;
  activeDefaults = options.defaults;
  activeOnApply = options.onApply;
  activeDraft = loadTableColumns(options.tableId, options.defaults);
  tableShadingDraft = isTableShadingEnabled();

  let overlay = document.getElementById(MODAL_ID);

  const close = (): void => {
    overlay?.classList.remove("sfe-modal-open");
  };

  const paint = (): void => {
    const prefs = overlay?.querySelector<HTMLElement>("[data-column-settings-prefs]");
    const list = overlay?.querySelector<HTMLElement>("[data-column-settings-list]");
    if (prefs) prefs.innerHTML = renderTableShadingPrefRow(tableShadingDraft);
    if (list) list.innerHTML = renderColumnSettingsBody(activeDraft, activeDefaults);
  };

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-column-settings-modal" role="dialog" aria-labelledby="sfe-column-settings-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-column-settings-title"></h3>
          <button type="button" class="sfe-modal-close" data-action="column-settings-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-column-settings-lead">Show, hide, reorder, and pin table columns. Pinned columns stay on the left while you scroll.</p>
        <div class="sfe-column-settings-prefs" data-column-settings-prefs></div>
        <div class="sfe-column-settings-list" data-column-settings-list></div>
        <div class="sfe-modal-actions sfe-column-settings-actions">
          <button type="button" class="ghost small" data-action="column-settings-reset">Reset</button>
          <button type="button" class="ghost small" data-action="column-settings-cancel">Cancel</button>
          <button type="button" class="small" data-action="column-settings-apply">Apply</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
  }

  if (!modalBound && overlay) {
    modalBound = true;
    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        close();
        return;
      }
      const actionEl = target.closest<HTMLElement>("[data-action]");
      const action = actionEl?.dataset.action;
      if (action === "column-settings-close" || action === "column-settings-cancel") close();
      if (action === "column-settings-reset") {
        activeDraft = activeDefaults.map((column) => withDefaultPinned(column, true));
        tableShadingDraft = false;
        paint();
      }
      if (action === "column-settings-apply") {
        saveTableColumns(activeTableId, activeDraft);
        setTableShadingEnabled(tableShadingDraft);
        activeOnApply?.(cloneColumns(activeDraft));
        close();
      }
      if (action === "column-pin") {
        const key = actionEl?.dataset.key;
        if (!key) return;
        const current = activeDraft.find((column) => column.key === key);
        activeDraft = toggleTableColumnPin(activeDraft, key, !(current?.pinned === true));
        const list = overlay?.querySelector<HTMLElement>("[data-column-settings-list]");
        if (list) list.innerHTML = renderColumnSettingsBody(activeDraft, activeDefaults);
      }
      if (action === "column-up" || action === "column-down") {
        const index = Number(actionEl?.dataset.index);
        if (!Number.isFinite(index)) return;
        activeDraft = moveTableColumn(
          activeDraft,
          index,
          action === "column-up" ? "up" : "down",
        );
        const list = overlay?.querySelector<HTMLElement>("[data-column-settings-list]");
        if (list) list.innerHTML = renderColumnSettingsBody(activeDraft, activeDefaults);
      }
    });

    overlay.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.dataset.tableShadingToggle !== undefined) {
        tableShadingDraft = target.checked;
        return;
      }
      const key = target.dataset.columnToggle;
      if (!key) return;
      activeDraft = toggleTableColumn(activeDraft, key, target.checked);
      const list = overlay?.querySelector<HTMLElement>("[data-column-settings-list]");
      if (list) list.innerHTML = renderColumnSettingsBody(activeDraft, activeDefaults);
    });
  }

  const title = overlay.querySelector("#sfe-column-settings-title");
  if (title) title.textContent = options.title;
  paint();
  overlay.classList.add("sfe-modal-open");
}

const MULTI_MODAL_ID = "sfe-multi-column-settings-modal";

type MultiColumnSection = {
  tableId: string;
  sectionTitle: string;
  defaults: ColumnDefinition[];
};

let multiDrafts = new Map<string, ColumnState[]>();
let multiDefaults = new Map<string, ColumnDefinition[]>();
let multiSections: MultiColumnSection[] = [];
let multiOnApply: (() => void) | null = null;
let multiModalBound = false;

export function openMultiColumnSettingsModal(options: {
  title: string;
  sections: MultiColumnSection[];
  onApply: () => void;
}): void {
  multiSections = options.sections.map((section) => {
    const tableId = effectiveWebTableId(section.tableId as TableColumnId);
    return {
      ...section,
      tableId,
      defaults: section.defaults.length ? section.defaults : getTableColumnDefaults(tableId),
    };
  });
  multiDrafts = new Map(
    multiSections.map((section) => [
      section.tableId,
      loadTableColumns(section.tableId, section.defaults),
    ]),
  );
  multiDefaults = new Map(multiSections.map((section) => [section.tableId, section.defaults]));
  multiOnApply = options.onApply;
  tableShadingDraft = isTableShadingEnabled();

  let overlay = document.getElementById(MULTI_MODAL_ID);
  const close = (): void => {
    overlay?.classList.remove("sfe-modal-open");
  };

  const paint = (): void => {
    const prefs = overlay?.querySelector<HTMLElement>("[data-column-settings-prefs]");
    const host = overlay?.querySelector<HTMLElement>("[data-multi-column-settings-sections]");
    if (prefs) prefs.innerHTML = renderTableShadingPrefRow(tableShadingDraft);
    if (!host) return;
    host.innerHTML = multiSections
      .map(
        (section) => `
        <section class="sfe-tab-prefs-section sfe-column-settings-section" data-column-section="${esc(section.tableId)}">
          <h4 class="wheel-settings-section-title">${esc(section.sectionTitle)}</h4>
          <div class="sfe-column-settings-list">${renderColumnSettingsBody(
            multiDrafts.get(section.tableId) ?? [],
            section.defaults,
            section.tableId,
          )}</div>
        </section>`,
      )
      .join("");
  };

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MULTI_MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-column-settings-modal sfe-multi-column-settings-modal" role="dialog" aria-labelledby="sfe-multi-column-settings-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-multi-column-settings-title"></h3>
          <button type="button" class="sfe-modal-close" data-action="multi-column-settings-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-column-settings-lead">Show, hide, reorder, and pin table columns. Pinned columns stay on the left while you scroll.</p>
        <div class="sfe-column-settings-prefs" data-column-settings-prefs></div>
        <div class="sfe-multi-column-settings-sections" data-multi-column-settings-sections></div>
        <div class="sfe-modal-actions sfe-column-settings-actions">
          <button type="button" class="ghost small" data-action="multi-column-settings-reset">Reset</button>
          <button type="button" class="ghost small" data-action="multi-column-settings-cancel">Cancel</button>
          <button type="button" class="small" data-action="multi-column-settings-apply">Apply</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
  }

  if (!multiModalBound && overlay) {
    multiModalBound = true;
    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        close();
        return;
      }
      const actionEl = target.closest<HTMLElement>("[data-action]");
      const action = actionEl?.dataset.action;
      if (action === "multi-column-settings-close" || action === "multi-column-settings-cancel") {
        close();
      }
      if (action === "multi-column-settings-reset") {
        for (const section of multiSections) {
          multiDrafts.set(
            section.tableId,
            section.defaults.map((column) => withDefaultPinned(column, true)),
          );
        }
        tableShadingDraft = false;
        paint();
      }
      if (action === "multi-column-settings-apply") {
        for (const section of multiSections) {
          const draft = multiDrafts.get(section.tableId);
          if (draft) saveTableColumns(section.tableId, draft);
        }
        setTableShadingEnabled(tableShadingDraft);
        multiOnApply?.();
        close();
      }
      if (action === "column-pin") {
        const sectionId = actionEl?.dataset.section;
        const key = actionEl?.dataset.key;
        if (!sectionId || !key) return;
        const draft = multiDrafts.get(sectionId);
        const defaults = multiDefaults.get(sectionId);
        if (!draft || !defaults) return;
        const current = draft.find((column) => column.key === key);
        multiDrafts.set(sectionId, toggleTableColumnPin(draft, key, !(current?.pinned === true)));
        const host = overlay?.querySelector<HTMLElement>("[data-multi-column-settings-sections]");
        if (host) paintColumnSettingsSection(host, sectionId, multiDrafts.get(sectionId)!, defaults);
      }
      if (action === "column-up" || action === "column-down") {
        const sectionId = actionEl?.dataset.section;
        const index = Number(actionEl?.dataset.index);
        if (!sectionId || !Number.isFinite(index)) return;
        const draft = multiDrafts.get(sectionId);
        const defaults = multiDefaults.get(sectionId);
        if (!draft || !defaults) return;
        multiDrafts.set(
          sectionId,
          moveTableColumn(draft, index, action === "column-up" ? "up" : "down"),
        );
        const host = overlay?.querySelector<HTMLElement>("[data-multi-column-settings-sections]");
        if (host) paintColumnSettingsSection(host, sectionId, multiDrafts.get(sectionId)!, defaults);
      }
    });

    overlay.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.dataset.tableShadingToggle !== undefined) {
        tableShadingDraft = target.checked;
        return;
      }
      const key = target.dataset.columnToggle;
      const sectionId = target.dataset.section;
      if (!key || !sectionId) return;
      const draft = multiDrafts.get(sectionId);
      const defaults = multiDefaults.get(sectionId);
      if (!draft || !defaults) return;
      multiDrafts.set(sectionId, toggleTableColumn(draft, key, target.checked));
      const host = overlay?.querySelector<HTMLElement>("[data-multi-column-settings-sections]");
      if (host) paintColumnSettingsSection(host, sectionId, multiDrafts.get(sectionId)!, defaults);
    });
  }

  const title = overlay.querySelector("#sfe-multi-column-settings-title");
  if (title) title.textContent = options.title;
  paint();
  overlay.classList.add("sfe-modal-open");
}

/** ☷ Configure columns — place in a table toolbar or panel header. */
export function mountColumnSettingsButton(
  host: HTMLElement,
  options: {
    tableId: string;
    title: string;
    defaults: ColumnDefinition[];
    onApply: () => void;
    label?: string;
    insertBefore?: HTMLElement;
  },
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "small ghost sfe-column-settings-btn";
  button.setAttribute("aria-label", options.title);
  button.title = options.title;
  button.textContent = options.label ?? "☷";
  button.addEventListener("click", () => {
    const tableId = effectiveWebTableId(options.tableId as TableColumnId);
    openColumnSettingsModal({
      tableId,
      title: options.title,
      defaults: getTableColumnDefaults(tableId),
      onApply: () => options.onApply(),
    });
  });
  if (options.insertBefore) {
    host.insertBefore(button, options.insertBefore);
  } else {
    host.appendChild(button);
  }
  return button;
}

/** ☷ immediately left of an existing Reset filters control. */
export function ensureColumnPickerBesideReset(
  resetButton: HTMLElement,
  options: {
    tableId: string;
    title: string;
    defaults: ColumnDefinition[];
    onApply: () => void;
  },
): HTMLButtonElement | null {
  if (!resetButton.isConnected) return null;

  let group = resetButton.closest<HTMLElement>(".sfe-filter-reset-actions");
  if (!group) {
    group = document.createElement("div");
    group.className = "sfe-filter-reset-actions";
    const parent = resetButton.parentElement;
    if (!parent) return null;
    parent.insertBefore(group, resetButton);
    group.appendChild(resetButton);
  }

  const existing = group.querySelector<HTMLButtonElement>(".sfe-column-settings-btn");
  if (existing) {
    return existing;
  }

  return mountColumnSettingsButton(group, {
    ...options,
    insertBefore: resetButton,
  });
}